import { Room } from '@wonderschool/common-base-types';
import { Button, DeleteIcon, Dialog } from '@wonderschool/common-base-ui';
import { UpdateRoomInput, useRoomUpdateMutation } from '@wonderschool/common-food-program-data-access';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { roomUpdated } from '../../redux/actions/roomActions';
import { logError } from '../../rollbar';

type DeleteRoomButtonProps = {
  room: Room;
  organizationId: string;
  hasStudentsOrStaff: boolean;
  onDeleted: () => void;
  disabled?: boolean;
  onlyOneRoom?: boolean;
};

export function DeleteRoomButton({
  room,
  organizationId,
  hasStudentsOrStaff,
  onDeleted,
  disabled = false,
  onlyOneRoom = false,
}: DeleteRoomButtonProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [roomUpdateMutation] = useRoomUpdateMutation();

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);

  const tryDeleteRoom = () => {
    setArchiveModalOpen(true);
  };

  const handleDeleteRoom = async () => {
    setArchiveModalOpen(false);
    setDeleteLoading(true);

    try {
      if (!organizationId || !room.id) {
        return;
      }

      // Write to backend
      const { id, location, name, enabled, ageRange, ratio, usesExactAgeRange } = room;
      const updateInput: UpdateRoomInput = {
        firebaseId: id,
        locationIdentifier: { firebaseId: location },
        isArchived: true,
        name,
        enabled,
        ageRange,
        ratio,
        usesExactAgeRange,
      };

      await roomUpdateMutation({
        variables: {
          input: updateInput,
        },
        onCompleted: () => {
          dispatch(roomUpdated(room));
          onDeleted();
        },
        onError: (error) => {
          logError('archiveRoom mutation error', error);
        },
      });

      dispatch(roomUpdated({ id: room.id }));
    } catch (error) {
      logError('archiveRoom firestore update error', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Compute title and description based on onlyOneRoom and hasStudentsOrStaff
  const title = onlyOneRoom ? t('roomsList.cannotDeleteRoom') : t('rooms.delete.confirmTitle');
  const description = onlyOneRoom
    ? t('roomsList.archiveRoomWarningOnlyOneRoom')
    : hasStudentsOrStaff
      ? t('roomsList.archiveRoomWarningWithStudentsOrStaff') + ' ' + t('roomsList.archiveRoomWarning')
      : t('roomsList.archiveRoomWarning');

  return (
    <>
      <Button
        extraClasses="w-full mt-8 ring-1 ring-red-500 text-red-500 hover:bg-red-50"
        onClick={tryDeleteRoom}
        data-testid="disable-btn"
        loading={deleteLoading}
        preIcon={<DeleteIcon className="size-5" />}
        disabled={disabled}
      >
        {t('rooms.delete.action')}
      </Button>

      <Dialog
        isOpen={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        hasCloseButton
        fullScreenOnMobile={true}
      >
        {/* If only 1 room, delete not allowed. If students or staff, show warning, but allow. Otherwise, show normal warning and allow */}
        <Dialog.Panel>
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description>{description}</Dialog.Description>
          <div className="my-6 flex w-full flex-col gap-2">
            {!onlyOneRoom && (
              <Button
                extraClasses="w-full bg-red-500 text-white ring-0 hover:bg-red-900"
                onClick={handleDeleteRoom}
                data-testid="yes-btn"
              >
                {t('rooms.delete.action')}
              </Button>
            )}
            <Button extraClasses="w-full" onClick={() => setArchiveModalOpen(false)} data-testid="no-btn">
              {t('common.cancel')}
            </Button>
          </div>
        </Dialog.Panel>
      </Dialog>
    </>
  );
}
